import React, { useState, useEffect, useContext } from 'react';
import Layout from '../../../components/Layout';
import ProviderAccountsTable from './components/ProviderAccountsTable';
import { GlobalStateContext } from '../../../context/GlobalStateContext';
import StatusMessage from '../../../components/StatusMessage';
import ProviderAccountSetupForm from './components/ProviderAccountSetupForm';
import {
  StaffFilterPopover,
  StaffFilterBadges,
  DEFAULT_STAFF_FILTERS,
} from './components/StaffFilterControls';
import useSessionState from '../../../hooks/useSessionState';
import { DASHBOARD_KEYS } from '../../../utils/dashboardViewState';

export default function ProviderUserDashboard() {
  const { refreshUserData, refreshProviderAccountsData, userData } = useContext(GlobalStateContext);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [isProviderAccountFormOpen, setIsProviderAccountFormOpen] = useState(false);

  // Staff search/filter values (name + email substrings, date-added range).
  // Session-persisted so the filter survives navigating to another page and back
  // — mirroring the patient dashboard — and wiped on sign-out via
  // clearDashboardViewState() since they can contain a staff member's name.
  const [staffFilters, setStaffFilters] = useSessionState(
    DASHBOARD_KEYS.staffFilters,
    () => ({ ...DEFAULT_STAFF_FILTERS })
  );
  const [isStaffFilterPopoverOpen, setIsStaffFilterPopoverOpen] = useState(false);

  const handleApplyStaffFilters = (formValues) => {
    setStaffFilters(formValues);
    setIsStaffFilterPopoverOpen(false);
  };

  const removeStaffFilter = (field) => {
    setStaffFilters((prev) => ({
      ...prev,
      [field]: field === 'dateRange' ? { startDate: null, endDate: null } : '',
    }));
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Load basic user info first (fast), then staff list
        await refreshUserData();
        await refreshProviderAccountsData();
      } finally {
        setIsPageLoading(false);
      }
    };
    fetchData();
  }, [refreshUserData, refreshProviderAccountsData]);

  if (userData) {
    console.log('Logged in user on Staff page:', {
      organisation: userData.organisation,
      accountType: userData.accountType,
      userType: userData.userType,
      clinicianId: userData.clinicianId,
      isEmailVerified: userData.isEmailVerified,
      isMFASetUp: userData.isMFASetUp
    });
  }

  const LoadingSpinner = () => (
    <div className="w-full h-24 flex items-center justify-center">
      <div className="inline-flex items-center px-4 py-2 bg-violet-100 text-slate-800 rounded-lg">
        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-slate-800" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <span className="font-semibold">Loading staff management...</span>
      </div>
    </div>
  );

  const WarningIcon = () => (
    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
    </svg>
  );

  // Loading state
  if (isPageLoading || !userData) {
    return (
      <Layout activePage="staff">
        <div className="flex-grow p-8">
          <LoadingSpinner />
        </div>
      </Layout>
    );
  }

  // Account type check
  if (userData.accountType !== 'Provider') {
    return (
      <Layout activePage="staff">
        <StatusMessage color="yellow" icon={<WarningIcon />}>
          This page is only accessible to Provider accounts.
        </StatusMessage>
      </Layout>
    );
  }

  // User type check:
  // - Staff management is intentionally shared between Org. Admins and Support staff.
  // - Clinicians (and all non‑Provider accounts) are blocked from this page to avoid
  //   accidental exposure of staff-level controls.
  const isOrgAdminOrSupport =
    userData.userType === 'Org. Admin' || userData.userType === 'Support';

  if (!isOrgAdminOrSupport) {
    return (
      <Layout activePage="staff">
        <StatusMessage color="yellow" icon={<WarningIcon />}>
          This page is only accessible to Organisation Administrators and Support staff.
        </StatusMessage>
      </Layout>
    );
  }

  return (
    <Layout activePage="staff">
      <div className="flex-grow p-8">
        <div className="flex-grow">
          <h1 className="text-2xl font-bold text-gray-900 mb-6">Staff Management</h1>
          
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-800">
              {userData.organisation} User Accounts
            </h2>

            {/* Right side - Filters and Actions (mirrors the patient dashboard) */}
            <div className="flex items-center gap-2">
              {/* Deletable badges for each active staff filter */}
              <div className="flex items-center gap-1">
                <StaffFilterBadges
                  activeFilters={staffFilters}
                  onRemoveFilter={removeStaffFilter}
                />
              </div>

              {/* Search & Filter popover */}
              <StaffFilterPopover
                isOpen={isStaffFilterPopoverOpen}
                onOpenChange={setIsStaffFilterPopoverOpen}
                onApplyFilters={handleApplyStaffFilters}
                initialValues={staffFilters}
              />

              <button
                className="flex text-sm items-center py-2 px-4 rounded-lg shadow-sm text-gray-900 border border-gray-300 hover:bg-gray-50 transition-all duration-300"
                onClick={() => setIsProviderAccountFormOpen(true)}
              >
                <span className="font-semibold">+ Set Up New User</span>
              </button>
            </div>
          </div>

          <ProviderAccountSetupForm
            isOpen={isProviderAccountFormOpen}
            onClose={() => setIsProviderAccountFormOpen(false)}
          />

          <ProviderAccountsTable activeFilters={staffFilters} />
        </div>
      </div>
    </Layout>
  );
}

